import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Brain, RefreshCw, AlertTriangle, Lightbulb, Target, MapPin } from "lucide-react";
import { toast } from "sonner";

export default function Analysis() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const fetch = async (refresh = false) => {
    if (refresh) setGenerating(true);
    else setLoading(true);
    try {
      const { data } = await api.post("/ai/analysis", { refresh });
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate analysis");
    } finally {
      setLoading(false);
      setGenerating(false);
    }
  };

  useEffect(() => {
    fetch(false);
  }, []);

  if (loading)
    return (
      <div className="space-y-4">
        <div className="h-10 w-72 bg-white/5 rounded animate-pulse" />
        <div className="grid grid-cols-2 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-48 bg-white/5 rounded-2xl animate-pulse" />
          ))}
        </div>
      </div>
    );

  const a = data?.analysis || {};

  return (
    <div className="space-y-8" data-testid="analysis-root">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-orange-400/80 mb-2">AI Business Analysis</div>
          <h1 className="font-heading text-3xl lg:text-5xl font-black tracking-tight">Your battle plan</h1>
          <p className="text-white/50 mt-2 max-w-2xl">
            SWOT, customer personas, marketing strategy and a 90-day roadmap — generated from your business profile.
          </p>
        </div>
        <button
          data-testid="analysis-regenerate"
          onClick={() => fetch(true)}
          disabled={generating}
          className="inline-flex items-center gap-2 glass px-4 py-2.5 rounded-lg text-sm hover:bg-white/5 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${generating ? "animate-spin" : ""}`} />
          {generating ? "Regenerating…" : "Regenerate"}
        </button>
      </header>

      {/* SWOT */}
      {a.swot && (
        <div className="grid md:grid-cols-2 gap-4">
          <SwotCard title="Strengths" color="#22C55E" items={a.swot.strengths} testid="swot-strengths" />
          <SwotCard title="Weaknesses" color="#F43F5E" items={a.swot.weaknesses} testid="swot-weaknesses" />
          <SwotCard title="Opportunities" color="#F97316" items={a.swot.opportunities} testid="swot-opportunities" />
          <SwotCard title="Threats" color="#F59E0B" items={a.swot.threats} testid="swot-threats" />
        </div>
      )}

      {/* Personas */}
      {Array.isArray(a.personas) && a.personas.length > 0 && (
        <section>
          <div className="text-xs uppercase tracking-[0.25em] text-white/40 mb-3">Customer personas</div>
          <div className="grid md:grid-cols-3 gap-4">
            {a.personas.map((p, i) => (
              <div key={i} className="glass rounded-2xl p-6" data-testid={`persona-${i}`}>
                <div className="h-10 w-10 rounded-lg brand-gradient grid place-items-center mb-3">
                  <Target className="h-5 w-5" />
                </div>
                <div className="font-heading font-bold text-lg">{p.name}</div>
                <div className="text-xs text-white/40 font-mono">{p.age_range}</div>
                <p className="text-sm text-white/70 mt-3">{p.description}</p>
                <div className="mt-4">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">Pain points</div>
                  <ul className="text-xs text-white/70 list-disc list-inside space-y-1">
                    {(p.pain_points || []).map((x, k) => (
                      <li key={k}>{x}</li>
                    ))}
                  </ul>
                </div>
                <div className="mt-3">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">Channels</div>
                  <div className="flex flex-wrap gap-1.5">
                    {(p.channels || []).map((c, k) => (
                      <span key={k} className="text-[11px] px-2 py-0.5 rounded-full glass">
                        {c}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Strategy */}
      {a.marketing_strategy && (
        <section className="glass rounded-2xl p-6">
          <div className="text-xs uppercase tracking-[0.25em] text-white/40 mb-3">Marketing strategy</div>
          <h3 className="font-heading text-2xl font-black mb-4">{a.marketing_strategy.positioning}</h3>
          <p className="text-white/70 leading-relaxed">{a.marketing_strategy.value_proposition}</p>
          <div className="grid md:grid-cols-2 gap-6 mt-6">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-2">Key messages</div>
              <ul className="space-y-2">
                {(a.marketing_strategy.key_messages || []).map((m, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-white/70">
                    <Lightbulb className="h-4 w-4 text-orange-400 mt-0.5 shrink-0" /> {m}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-2">Primary channels</div>
              <div className="flex flex-wrap gap-2">
                {(a.marketing_strategy.channels || []).map((c, i) => (
                  <span key={i} className="text-xs px-3 py-1 rounded-full brand-gradient">
                    {c}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* SEO */}
      {a.seo_strategy && (
        <section className="grid md:grid-cols-3 gap-4">
          <KeyCard title="Primary keywords" items={a.seo_strategy.primary_keywords} icon={MapPin} />
          <KeyCard title="Content topics" items={a.seo_strategy.content_topics} icon={Lightbulb} />
          <KeyCard title="Local SEO" items={a.seo_strategy.local_seo_tips} icon={MapPin} />
        </section>
      )}

      {/* Roadmap */}
      {Array.isArray(a.roadmap_90_day) && (
        <section>
          <div className="text-xs uppercase tracking-[0.25em] text-white/40 mb-3">90-day roadmap</div>
          <div className="grid md:grid-cols-3 gap-4">
            {a.roadmap_90_day.map((phase, i) => (
              <div key={i} className="glass rounded-2xl p-6">
                <div className="text-xs font-mono text-orange-400 mb-2">PHASE {i + 1}</div>
                <h4 className="font-heading font-bold text-lg mb-4">{phase.phase}</h4>
                <ul className="space-y-2 text-sm text-white/70">
                  {(phase.actions || []).map((act, k) => (
                    <li key={k} className="flex items-start gap-2">
                      <span className="text-orange-500 mt-1">→</span> {act}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Growth ideas */}
      {Array.isArray(a.growth_ideas) && (
        <section className="glass rounded-2xl p-6">
          <div className="text-xs uppercase tracking-[0.25em] text-white/40 mb-3">Growth ideas</div>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-3">
            {a.growth_ideas.map((g, i) => (
              <div key={i} className="border border-white/10 rounded-lg p-4 text-sm text-white/80 hover:bg-white/[0.03] transition">
                {g}
              </div>
            ))}
          </div>
        </section>
      )}

      {a.raw && (
        <div className="glass rounded-2xl p-6 text-sm text-white/50">
          <div className="flex items-center gap-2 mb-2 text-white/70">
            <AlertTriangle className="h-4 w-4" /> Raw AI response (couldn't parse structured JSON)
          </div>
          <pre className="whitespace-pre-wrap font-mono text-xs">{a.raw}</pre>
        </div>
      )}
    </div>
  );
}

function SwotCard({ title, items = [], color, testid }) {
  return (
    <div className="glass rounded-2xl p-6" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <span className="h-2 w-2 rounded-full" style={{ background: color }} />
        <div className="text-xs uppercase tracking-[0.25em] text-white/50">{title}</div>
      </div>
      <ul className="space-y-2 text-sm text-white/80">
        {items.map((it, i) => (
          <li key={i} className="leading-relaxed">• {it}</li>
        ))}
      </ul>
    </div>
  );
}

function KeyCard({ title, items = [], icon: Icon }) {
  return (
    <div className="glass rounded-2xl p-6">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="h-4 w-4 text-orange-400" />
        <div className="text-xs uppercase tracking-[0.25em] text-white/50">{title}</div>
      </div>
      <ul className="space-y-2 text-sm text-white/80">
        {items.map((it, i) => (
          <li key={i}>• {it}</li>
        ))}
      </ul>
    </div>
  );
}
